import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Loader, Package, Search as SearchIcon } from 'lucide-react';
import BikeFilter from '../components/BikeFilter';
import { useLanguage } from '../context/LanguageContext';
import { getProductPath } from '../utils/helpers';

const API = process.env.REACT_APP_BACKEND_URL;

const T = {
  it: {
    title: 'Trova ricambi per la tua moto',
    metaDescription: 'Cerca ricambi e accessori motocross compatibili con la tua moto. Seleziona marca, anno e modello per trovare tutti i prodotti ALLMX dedicati.',
    heroText: 'Più di 760 ricambi motocross filtrabili per la tua moto specifica. Marche KTM, Husqvarna, Yamaha, Honda, Kawasaki, Suzuki, Beta, Gas Gas, TM e molte altre.',
    results: 'Risultati',
    noResults: 'Nessun prodotto trovato per questa configurazione',
    pickBike: 'Seleziona la tua moto per vedere i ricambi compatibili.',
    productsFor: 'Ricambi per',
    seeAll: 'Vedi tutto il catalogo',
    viewProduct: 'Vedi prodotto',
  },
  en: {
    title: 'Find parts for your bike',
    metaDescription: 'Search motocross parts and accessories compatible with your bike. Pick brand, year and model to find all ALLMX dedicated products.',
    heroText: 'Over 760 motocross parts filterable for your specific bike. Brands: KTM, Husqvarna, Yamaha, Honda, Kawasaki, Suzuki, Beta, Gas Gas, TM and many more.',
    results: 'Results',
    noResults: 'No products found for this selection',
    pickBike: 'Pick your bike to see compatible parts.',
    productsFor: 'Parts for',
    seeAll: 'See all catalog',
    viewProduct: 'View product',
  },
  es: {
    title: 'Encuentra recambios para tu moto',
    metaDescription: 'Busca recambios y accesorios de motocross compatibles con tu moto. Elige marca, año y modelo para encontrar todos los productos ALLMX dedicados.',
    heroText: 'Más de 760 recambios de motocross filtrables para tu moto específica. Marcas: KTM, Husqvarna, Yamaha, Honda, Kawasaki, Suzuki, Beta, Gas Gas, TM y muchas más.',
    results: 'Resultados',
    noResults: 'Ningún producto encontrado para esta selección',
    pickBike: 'Selecciona tu moto para ver los recambios compatibles.',
    productsFor: 'Recambios para',
    seeAll: 'Ver todo el catálogo',
    viewProduct: 'Ver producto',
  },
};

export default function BikeFinder() {
  const { language } = useLanguage();
  const t = T[language] || T.it;
  const [searchParams, setSearchParams] = useSearchParams();

  const [filter, setFilter] = useState({
    brand: searchParams.get('brand') || '',
    year: searchParams.get('year') || '',
    model: searchParams.get('model') || '',
  });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [queried, setQueried] = useState(false);

  const runSearch = useCallback(async (f) => {
    if (!f.brand) {
      setProducts([]);
      setQueried(false);
      return;
    }
    setLoading(true);
    setQueried(true);
    try {
      const params = { brand: f.brand };
      if (f.year) params.year = f.year;
      if (f.model) params.model = f.model;
      const r = await axios.get(`${API}/api/bikes/products`, { params });
      setProducts(Array.isArray(r.data?.products) ? r.data.products : []);
    } catch (_) {
      setProducts([]);
    }
    setLoading(false);
  }, []);

  // Auto-run search whenever filter changes (also on first mount if URL has params)
  useEffect(() => {
    runSearch(filter);
    // Reflect state into URL for shareability
    const params = {};
    if (filter.brand) params.brand = filter.brand;
    if (filter.year) params.year = filter.year;
    if (filter.model) params.model = filter.model;
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.brand, filter.year, filter.model]);

  const pageTitle = filter.brand
    ? `${t.productsFor} ${filter.brand}${filter.model ? ' ' + filter.model : ''}${filter.year ? ' ' + filter.year : ''} — ALLMX`
    : `${t.title} — ALLMX`;

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={t.metaDescription} />
        <link rel="canonical" href={`https://allmx.it/${language === 'it' ? 'trova-ricambi-per-moto' : language === 'en' ? 'find-parts-by-bike' : 'buscar-recambios-por-moto'}`} />
      </Helmet>

      <section className="pt-10 pb-6 px-4 border-b border-white/10">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <SearchIcon size={28} className="text-primary" />
            <h1 className="text-3xl md:text-4xl font-black uppercase tracking-tight">
              {t.title}
            </h1>
          </div>
          <p className="text-white/70 text-sm md:text-base max-w-3xl">
            {t.heroText}
          </p>
        </div>
      </section>

      <section className="px-4 py-6">
        <div className="max-w-5xl mx-auto">
          <BikeFilter
            value={filter}
            onChange={setFilter}
            variant="hero"
          />
        </div>
      </section>

      <section className="px-4 pb-12">
        <div className="max-w-5xl mx-auto">
          {!queried && (
            <div className="text-center py-16 text-white/50">
              <Package size={48} className="mx-auto mb-4 opacity-30" />
              <p>{t.pickBike}</p>
            </div>
          )}

          {queried && loading && (
            <div className="text-center py-16">
              <Loader size={32} className="mx-auto animate-spin text-primary" />
            </div>
          )}

          {queried && !loading && products.length === 0 && (
            <div className="text-center py-16 text-white/50">
              <p className="mb-4">{t.noResults}</p>
              <Link to="/products" className="text-primary hover:underline">
                {t.seeAll}
              </Link>
            </div>
          )}

          {queried && !loading && products.length > 0 && (
            <>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">
                  {t.results} <span className="text-primary">({products.length})</span>
                </h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {products.map((p) => (
                  <Link
                    key={p.sku}
                    to={getProductPath(p)}
                    className="group block bg-white/5 border border-white/10 rounded-lg overflow-hidden hover:border-primary/50 hover:bg-white/8 transition-all"
                    data-testid={`bike-finder-product-${p.sku}`}
                  >
                    <div className="aspect-square bg-black/40 overflow-hidden">
                      {p.images?.[0] && (
                        <img
                          src={p.images[0]}
                          alt={p.name_it || p.name_en || p.sku}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          loading="lazy"
                        />
                      )}
                    </div>
                    <div className="p-3">
                      <div className="text-xs text-primary mb-1 uppercase tracking-wider">
                        {p.category}
                      </div>
                      <div className="text-sm font-semibold line-clamp-2 min-h-[2.5rem]">
                        {language === 'it' ? p.name_it : language === 'en' ? (p.name_en || p.name_it) : (p.name_es || p.name_it)}
                      </div>
                      <div className="text-primary font-bold mt-2">
                        € {p.price?.toFixed(2) ?? '—'}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
